import React, { useState } from 'react';
import Header from '../components/Header';
import { useQuery } from '@tanstack/react-query';
import { portalCandidateService } from '../api/portalCandidateService';
import Toast from '../components/Toast';

type AssessmentScheduleResponse = {
  success: boolean;
  data: Array<{
    assessment: {
      id: number;
      name: string;
      date: string;
      location?: string | null;
    };
    participantId: number;
    slots: Array<{
      id: number;
      dayIndex: number;
      startTime: string;
      endTime: string;
      label?: string | null;
      slotType: 'session' | 'break' | 'meal' | 'other';
      track?: string | null;
      interviewer?: string | null;
      location?: string | null;
    }>;
  }>;
};

type AssessmentInterviewResponse = {
  success: boolean;
  data: Array<{
    assessment: {
      id: number;
      name: string;
      date: string;
      location?: string | null;
    };
    participantId: number;
    interviews: Array<{
      slotId: number;
      dayIndex: number;
      startTime: string;
      endTime: string;
      location?: string | null;
      interviewPeriodIndex?: number | null;
      interviews: Array<{
        role?: { id: number; displayName?: string | null } | null;
        participantPerson?: 'candidate' | 'spouse' | 'couple' | null;
        assessor?: { name?: string | null; email?: string | null } | null;
      }>;
    }>;
  }>;
};

const formatTime = (t: string) => {
  // "HH:mm:ss" -> "h:mm AM/PM"
  const [hh, mm] = (t || '').split(':');
  const d = new Date();
  d.setHours(parseInt(hh || '0', 10), parseInt(mm || '0', 10), 0, 0);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const addDays = (dateStr: string, days: number) => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d;
};

const AssessmentSchedule: React.FC = () => {
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [attendanceConfirmed, setAttendanceConfirmed] = useState(false);
  const [hotelNeeded, setHotelNeeded] = useState(false);
  const [hotelSundayNight, setHotelSundayNight] = useState(false);
  const [hotelMondayNight, setHotelMondayNight] = useState(false);
  const [dietaryRestrictions, setDietaryRestrictions] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [isSubmittingAttendance, setIsSubmittingAttendance] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['portalCandidate', 'assessmentSchedule'],
    queryFn: async () => (await portalCandidateService.getAssessmentSchedule()) as AssessmentScheduleResponse,
  });

  const { data: interviewData } = useQuery({
    queryKey: ['portalCandidate', 'assessmentInterviews'],
    queryFn: async () => (await portalCandidateService.getAssessmentInterviews()) as AssessmentInterviewResponse,
  });

  const { data: attendanceInfo, refetch: refetchAttendance } = useQuery({
    queryKey: ['portalCandidate', 'assessmentAttendance'],
    queryFn: async () => await portalCandidateService.getAssessmentAttendance(),
  });

  const attendanceAssessment = attendanceInfo?.data?.assessment || null;
  const assessmentAttendance = attendanceInfo?.data?.attendance || null;

  const openAttendanceModal = () => {
    setAttendanceConfirmed(assessmentAttendance?.isAttending || false);
    setHotelNeeded(assessmentAttendance?.hotelNeeded || false);
    setHotelSundayNight(assessmentAttendance?.hotelSundayNight || false);
    setHotelMondayNight(assessmentAttendance?.hotelMondayNight || false);
    setDietaryRestrictions(assessmentAttendance?.dietaryRestrictions || '');
    setShowAttendanceModal(true);
  };

  const handleSubmitAttendance = async () => {
    if (!attendanceConfirmed) {
      setToast({ message: 'Please confirm your attendance to continue.', type: 'error' });
      return;
    }

    try {
      setIsSubmittingAttendance(true);
      await portalCandidateService.submitAssessmentAttendance({
        isAttending: true,
        hotelNeeded,
        hotelSundayNight,
        hotelMondayNight,
        dietaryRestrictions: dietaryRestrictions.trim() || null,
      });
      await refetchAttendance();
      setToast({ message: 'Attendance confirmation saved.', type: 'success' });
      setShowAttendanceModal(false);
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Unable to save attendance. Please try again.';
      setToast({ message, type: 'error' });
    } finally {
      setIsSubmittingAttendance(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
      <div className="max-w-5xl mx-auto py-6 px-4 sm:px-6 lg:px-8 space-y-6">
        <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Assessment Schedule</h1>
          <p className="text-gray-600 mt-2">
            This shows schedule items that apply to everyone plus your assigned sessions.
          </p>
        </div>

        {attendanceAssessment && (
          <div className="bg-white shadow-lg rounded-xl p-4 sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                  Assessment Attendance Confirmation
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {new Date(attendanceAssessment.date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                  {attendanceAssessment.location ? ` • ${attendanceAssessment.location}` : ''}
                </p>
                {assessmentAttendance?.isAttending && (
                  <div className="mt-2 inline-flex items-center text-sm font-medium text-green-700">
                    <svg className="h-4 w-4 mr-1.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Attendance confirmed
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={openAttendanceModal}
                className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700"
              >
                {assessmentAttendance?.isAttending ? 'Edit Confirmation' : 'Confirm Attendance'}
              </button>
            </div>
          </div>
        )}

        {isLoading && (
          <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 text-gray-700">
            Loading schedule…
          </div>
        )}

        {error && (
          <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 text-red-700">
            Failed to load schedule.
          </div>
        )}

        {!isLoading && !error && (
          <>
            {(data?.data || []).length === 0 ? (
              <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 text-gray-700">
                You’re not currently added to an assessment event.
              </div>
            ) : (
              (data?.data || []).map((item) => {
                const interviewItem = (interviewData?.data || []).find(
                  (entry) => entry.assessment?.id === item.assessment.id
                );
                const interviewSlots = (interviewItem?.interviews || []).slice().sort((a, b) => {
                  if (a.dayIndex !== b.dayIndex) return a.dayIndex - b.dayIndex;
                  return (a.startTime || '').localeCompare(b.startTime || '');
                });
                const formatPersonLabel = (person?: 'candidate' | 'spouse' | 'couple' | null) => {
                  if (person === 'candidate') return 'Candidate';
                  if (person === 'spouse') return 'Spouse';
                  if (person === 'couple') return 'Couple';
                  return 'Interview';
                };

                const dayOneBefore = [
                  { time: '8:30 AM', label: 'Registration and Breakfast' },
                  { time: '9:00 AM', label: 'Opening and Introductions' },
                  { time: '9:30 AM', label: 'Calling Exercise' },
                ];
                const dayOneAfter = [
                  { time: '5:15 PM', label: 'Commissioning Exercise' },
                ];
                const dayTwoRows = [
                  { time: '8:45 AM', label: 'Breakfast' },
                  { time: '9:00 AM', label: 'Vision Casting Exercise' },
                  { time: '10:00 AM', label: 'Break' },
                  { time: '10:10 AM', label: 'Speed Dating' },
                  { time: '11:00 AM', label: 'Self Leadership' },
                  { time: '11:30 AM', label: 'Closing Exercise' },
                  { time: '12:00 PM', label: 'Dismissal by 12 Noon (Lunch not provided)' },
                ];
                const renderScheduleTable = (rows: Array<{ time: string; label: string }>) => (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-white">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Time
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Session
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {rows.map((row) => (
                          <tr key={`${row.time}-${row.label}`} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{row.time}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{row.label}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );

                const interviewSlotByStart = new Map(
                  interviewSlots
                    .filter((slot) => !!slot.startTime)
                    .map((slot) => [slot.startTime as string, slot])
                );
                const dayOneInterviewRows: Array<
                  | { time: string; type: 'interview'; start: string }
                  | { time: string; type: 'break'; label: string }
                > = [
                  { time: '10:30 AM', type: 'interview', start: '10:30' },
                  { time: '11:20 AM', type: 'break', label: 'Break' },
                  { time: '11:30 AM', type: 'interview', start: '11:30' },
                  { time: '12:30 PM', type: 'break', label: 'Lunch' },
                  { time: '1:15 PM', type: 'interview', start: '13:15' },
                  { time: '2:05 PM', type: 'break', label: 'Break' },
                  { time: '2:15 PM', type: 'interview', start: '14:15' },
                  { time: '3:05 PM', type: 'break', label: 'Break' },
                  { time: '3:15 PM', type: 'interview', start: '15:15' },
                  { time: '4:05 PM', type: 'break', label: 'Break' },
                  { time: '4:15 PM', type: 'interview', start: '16:15' },
                ];

                return (
                <div key={item.assessment.id} className="bg-white shadow-lg rounded-xl p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                    <div>
                      <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                        {item.assessment.name}
                      </h2>
                      <div className="text-sm text-gray-600 mt-1">
                        {new Date(item.assessment.date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                        {item.assessment.location ? ` • ${item.assessment.location}` : ''}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                        <div className="text-sm font-semibold text-gray-900">Day 1 - Monday</div>
                        <div className="text-sm text-gray-600">
                          {addDays(item.assessment.date, 0).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </div>
                      </div>
                      {renderScheduleTable(dayOneBefore)}
                      <div className="border-t border-gray-200">
                        <div className="px-4 py-3 bg-primary-50 border-b border-primary-100">
                          <h3 className="text-sm font-semibold text-primary-900">Interview Blocks</h3>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-primary-100">
                            <thead className="bg-white">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                  Time
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                  Interview
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-primary-100">
                              {dayOneInterviewRows.length === 0 ? (
                                <tr>
                                  <td className="px-4 py-3 text-sm text-gray-700" colSpan={2}>
                                    Interviews will appear here once they are assigned.
                                  </td>
                                </tr>
                              ) : (
                                dayOneInterviewRows.map((row) => {
                                  if (row.type === 'break') {
                                    return (
                                      <tr key={`${row.time}-${row.label}`} className="bg-gray-50">
                                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                                          {row.time}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-700 font-medium">
                                          {row.label}
                                        </td>
                                      </tr>
                                    );
                                  }
                                  const slot = row.type === 'interview'
                                    ? interviewSlotByStart.get(row.start)
                                    : undefined;
                                  return (
                                    <tr key={`${row.time}-interview`} className="hover:bg-primary-50/40">
                                      <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                                        {row.time}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-900">
                                        {!slot || (slot.interviews || []).length === 0 ? (
                                          <div className="font-medium">Free Block</div>
                                        ) : (
                                          <div className="space-y-2">
                                            {slot.interviews.map((interview, index) => (
                                              <div key={`${slot.slotId}-${index}`}>
                                                <div className="font-medium">
                                                  {formatPersonLabel(interview.participantPerson)}:{' '}
                                                  {interview.role?.displayName || 'Interview'}
                                                </div>
                                                {interview.assessor?.name && (
                                                  <div className="text-xs text-gray-600 mt-1">
                                                    Assessor: {interview.assessor.name}
                                                  </div>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="border-t border-gray-200">
                        {renderScheduleTable(dayOneAfter)}
                      </div>
                    </div>

                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                        <div className="text-sm font-semibold text-gray-900">Day 2 - Tuesday</div>
                        <div className="text-sm text-gray-600">
                          {addDays(item.assessment.date, 1).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </div>
                      </div>
                      {renderScheduleTable(dayTwoRows)}
                    </div>
                  </div>
                </div>
              )})
            )}
          </>
        )}
      </div>

      {showAttendanceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50 px-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Confirm Attendance</h3>
              <p className="text-sm text-gray-600 mt-1">
                Please review the requirements and confirm your attendance.
              </p>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800 space-y-1">
                <p>- No kids</p>
                <p>- Wife and husband both must attend</p>
                <p>- We need 100% participation for the full duration of the event</p>
              </div>

              <label className="flex items-start gap-3 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 rounded"
                  checked={attendanceConfirmed}
                  onChange={(event) => setAttendanceConfirmed(event.target.checked)}
                />
                <span>I understand and confirm my attendance.</span>
              </label>

              <div className="space-y-3">
                <label className="flex items-start gap-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 rounded"
                    checked={hotelNeeded}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setHotelNeeded(checked);
                      if (!checked) {
                        setHotelSundayNight(false);
                        setHotelMondayNight(false);
                      }
                    }}
                  />
                  <span>I need a hotel reservation.</span>
                </label>

                {hotelNeeded && (
                  <div className="pl-7 space-y-2 text-sm text-gray-700">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                        checked={hotelSundayNight}
                        onChange={(event) => setHotelSundayNight(event.target.checked)}
                      />
                      Sunday night
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                        checked={hotelMondayNight}
                        onChange={(event) => setHotelMondayNight(event.target.checked)}
                      />
                      Monday night
                    </label>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dietary considerations
                </label>
                <textarea
                  rows={3}
                  value={dietaryRestrictions}
                  onChange={(event) => setDietaryRestrictions(event.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="List any dietary restrictions or allergies."
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowAttendanceModal(false)}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitAttendance}
                disabled={isSubmittingAttendance}
                className="px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
              >
                {isSubmittingAttendance ? 'Saving...' : 'Confirm Attendance'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentSchedule;

